import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  FileDown,
  ImageDown,
  Loader2,
  Printer,
  RotateCcw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import PrintOrderList from "@/components/PrintOrderList";
import {
  formatPaperDate, isValidPaperDate, moveItem, syncOrder,
} from "@/lib/printOrder";

/* ---------------------------------------------------------------------------
 * Convert-to-Print
 *
 * Two panes:
 *   • LEFT: paginated Library list. Multi-select persists across pages
 *     because we keep the selected IDs in a Set, not a slice of the
 *     visible page.
 *   • RIGHT: settings for this batch (word count) + Process button +
 *     job-progress panel + prior jobs (with re-download).
 *
 * The processing itself is async on the backend — POST creates a job,
 * the page polls every 3 s until the job is 'ready' or 'error', then
 * offers a Download button that streams the .docx.
 *
 * 2026-10-01: the selected articles are put in print order under "Show
 * selected" (drag or up/down — components/PrintOrderList.jsx) and the
 * .docx and images .zip follow that order. The editor also enters the
 * paper date, and the backend rewrites time references for a reader
 * holding the paper on that date (backend/print_dates.py); what it
 * changed is listed per article in the job panel.
 * ------------------------------------------------------------------------- */

const PAGE_SIZE = 20;
// Stable empty map so memos keyed on print usage don't recompute on
// every render while the query is loading.
const EMPTY = Object.freeze({});

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function formatShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
}

/**
 * Marks an article that has already been converted to print, so a
 * second pass for "two or three more" doesn't pull a repeat.
 *
 * PRINTED means the article is really inside a .docx that was produced
 * (it came back "ok" in a job that reached "ready"). CONVERTING means it
 * is in a job that is still running. An article that errored is not
 * marked — it never made it into the file. See backend/print_usage.py.
 *
 * Clicking PRINTED unmarks the article — for a leftover that was
 * converted but didn't make the paper. That stamps the article, never
 * the export history, and leaves a muted UNMARKED badge that restores
 * the marks when clicked. A later export marks it again as normal.
 *
 * The badges are buttons inside the row's <label>; preventDefault stops
 * the click from also toggling the row checkbox.
 */
function PrintedBadges({ usage, clearedAt, articleId, onToggle, busy }) {
  const handle = (e, cleared) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle(cleared);
  };
  if (!usage) {
    if (!clearedAt) return null;
    return (
      <button
        type="button"
        data-testid={`print-restore-badge-${articleId}`}
        onClick={(e) => handle(e, false)}
        disabled={busy}
        title={`Unmarked on ${formatShortDate(clearedAt)}. Click to restore its print history.`}
        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 bg-[var(--secondary)] text-[var(--ink-muted)] border border-[var(--border)] hover:text-[var(--ink)] disabled:opacity-50"
      >
        <RotateCcw className="w-3 h-3 shrink-0" />
        unmarked
      </button>
    );
  }
  const printed = usage.status === "printed";
  const when = formatShortDate(usage.last_printed_at);
  const count = usage.print_count || 0;
  return (
    <span className="shrink-0 inline-flex items-center gap-1 flex-wrap justify-end">
      {printed && (
        <button
          type="button"
          data-testid={`print-used-badge-${articleId}`}
          onClick={(e) => handle(e, true)}
          disabled={busy}
          title={
            `Already converted to print in ${count} export${count === 1 ? "" : "s"}` +
            (when ? ` — most recent ${when}` : "") +
            ". Click to unmark it if it was a leftover (the export history is not changed)."
          }
          className="inline-flex items-center gap-1 text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 border bg-violet-50 text-violet-800 border-violet-300 hover:bg-violet-100 disabled:opacity-50"
        >
          <Printer className="w-3 h-3 shrink-0" />
          printed
          {count > 1 ? ` ×${count}` : ""}
          {when ? ` · ${when}` : ""}
        </button>
      )}
      {usage.in_progress && (
        <span
          data-testid={`print-converting-badge-${articleId}`}
          title="In an export that is converting right now."
          className="inline-flex items-center gap-1 text-[10px] font-mono-display uppercase tracking-wider px-1.5 py-0.5 border bg-amber-50 text-amber-800 border-amber-300"
        >
          <Loader2 className="w-3 h-3 shrink-0 animate-spin" />
          converting
        </span>
      )}
    </span>
  );
}

export default function PrintEditionPage() {
  const qc = useQueryClient();

  // Full article list (already scoped to the active property server-side).
  const { data: allArticles = [], isLoading } = useQuery({
    queryKey: ["articles"],
    queryFn: api.listArticles,
  });

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    staleTime: 5 * 60 * 1000,
  });
  const defaultWords =
    Number(settings?.print_edition_default_words) || 500;

  // Which articles are already in a past export (or converting now),
  // plus which ones the editor unmarked as leftovers.
  // Declared above every memo that reads it.
  const { data: printUsageData } = useQuery({
    queryKey: ["print-usage"],
    queryFn: api.getPrintUsage,
  });
  const printUsage = printUsageData?.usage || EMPTY;
  const printClearedAt = printUsageData?.cleared_at || EMPTY;
  const isProcessed = (id) => !!printUsage[id];

  // Unmark (leftover) / restore. `busyIds` disables the badges in flight.
  const [busyIds, setBusyIds] = useState(() => new Set());
  const setCleared = useMutation({
    mutationFn: ({ ids, cleared }) => api.setPrintUsageCleared(ids, cleared),
    onMutate: ({ ids }) => {
      setBusyIds((s) => new Set([...s, ...ids]));
    },
    onSuccess: (_r, { ids, cleared }) => {
      toast.success(
        cleared
          ? `Unmarked ${ids.length} article${ids.length === 1 ? "" : "s"} — available for the next edition.`
          : "Print history restored.",
      );
    },
    onError: (e) => {
      toast.error(
        e?.response?.data?.detail || e.message || "Couldn't update the mark.",
      );
    },
    onSettled: (_r, _e, { ids }) => {
      qc.invalidateQueries({ queryKey: ["print-usage"] });
      setBusyIds((s) => {
        const n = new Set(s);
        ids.forEach((id) => n.delete(id));
        return n;
      });
    },
  });

  // -------- Selection state (persists across pagination + search) -------- //
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggle = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const clearAll = () => setSelectedIds(new Set());

  // -------- Print order (arranged under "Show selected") -------- //
  // `orderState` remembers the editor's arrangement; syncOrder keeps it
  // in step with the selection (new picks go to the end).
  const [orderState, setOrderState] = useState([]);
  const orderedIds = useMemo(
    () => syncOrder(orderState, selectedIds),
    [orderState, selectedIds],
  );
  const moveSelected = (from, to) => setOrderState(moveItem(orderedIds, from, to));

  // -------- Paper date -------- //
  const [paperDate, setPaperDate] = useState("");
  const paperDateOk = isValidPaperDate(paperDate);

  // -------- Search + pagination -------- //
  const [search, setSearch] = useState("");
  const [hideProcessed, setHideProcessed] = useState(false);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, hideProcessed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = allArticles;
    if (hideProcessed) list = list.filter((a) => !printUsage[a.id]);
    if (!q) return list;
    return list.filter(
      (a) =>
        (a.headline || "").toLowerCase().includes(q) ||
        (a.writer_name || "").toLowerCase().includes(q),
    );
  }, [allArticles, search, hideProcessed, printUsage]);

  const processedCount = useMemo(
    () => allArticles.filter((a) => printUsage[a.id]).length,
    [allArticles, printUsage],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSlice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const articleById = useMemo(
    () => new Map(allArticles.map((a) => [a.id, a])),
    [allArticles],
  );
  // In print order — the same order the .docx and the .zip use.
  const selectedArticles = orderedIds
    .map((id) => articleById.get(id))
    .filter(Boolean);
  const selectedProcessedCount = selectedArticles.filter((a) =>
    isProcessed(a.id),
  ).length;
  // Only PRINTED ones can be unmarked; CONVERTING ones aren't done yet.
  const selectedPrintedIds = selectedArticles
    .filter((a) => printUsage[a.id]?.status === "printed")
    .map((a) => a.id);

  // -------- Word count for this batch -------- //
  const [wordCount, setWordCount] = useState(defaultWords);
  useEffect(() => { setWordCount(defaultWords); }, [defaultWords]);

  // -------- Job creation + polling -------- //
  const [activeJobId, setActiveJobId] = useState(null);

  const createJob = useMutation({
    mutationFn: () =>
      api.createPrintJob({
        article_ids: orderedIds,
        word_count: Math.max(80, Math.min(2500, Number(wordCount) || 500)),
        paper_date: paperDate.trim(),
      }),
    onSuccess: (job) => {
      setActiveJobId(job.id);
      qc.invalidateQueries({ queryKey: ["print-jobs"] });
      qc.invalidateQueries({ queryKey: ["print-usage"] });
      toast.success(`Converting ${job.total} article${job.total === 1 ? "" : "s"}…`);
    },
    onError: (e) => {
      toast.error(
        e?.response?.data?.detail || e.message || "Couldn't start the job.",
      );
    },
  });

  const { data: activeJob } = useQuery({
    queryKey: ["print-job", activeJobId],
    queryFn: () => api.getPrintJob(activeJobId),
    enabled: !!activeJobId,
    // Poll every 3s while pending. Stop once ready / error.
    refetchInterval: (q) => {
      const d = q?.state?.data;
      if (!d) return 3000;
      return d.status === "pending" ? 3000 : false;
    },
  });

  // When a job finishes, kick off the download automatically so the
  // editor doesn't have to hunt for a button — one click, one .docx.
  const [downloadedJobs, setDownloadedJobs] = useState(() => new Set());
  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status === "ready" && !downloadedJobs.has(activeJob.id)) {
      const url = api.printJobDownloadUrl(activeJob.id);
      window.location.assign(url);
      setDownloadedJobs((s) => new Set([...s, activeJob.id]));
      // The articles in this export are now PRINTED — refresh badges.
      qc.invalidateQueries({ queryKey: ["print-usage"] });
      const failed = (activeJob.articles || []).filter(
        (a) => a.status === "error",
      ).length;
      if (failed > 0) {
        toast.warning(
          `${failed} article${failed === 1 ? "" : "s"} failed to convert. See the panel for details.`,
        );
      } else {
        toast.success("Print edition ready — downloading .docx.");
      }
    } else if (activeJob.status === "error") {
      toast.error(activeJob.error || "Job failed.");
      // Clear the CONVERTING badges — a failed job marks nothing.
      qc.invalidateQueries({ queryKey: ["print-usage"] });
    }
  }, [activeJob, downloadedJobs, qc]);

  // -------- Prior jobs list -------- //
  const { data: priorJobs = [] } = useQuery({
    queryKey: ["print-jobs"],
    queryFn: api.listPrintJobs,
    refetchInterval: activeJob?.status === "pending" ? 5000 : false,
  });

  const deleteJob = useMutation({
    mutationFn: (id) => api.deletePrintJob(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ["print-jobs"] });
      // Deleting an export is how its marks are cleared.
      qc.invalidateQueries({ queryKey: ["print-usage"] });
      if (id === activeJobId) setActiveJobId(null);
    },
  });

  const canProcess = selectedIds.size > 0 && paperDateOk && !createJob.isPending;

  return (
    <div className="flex-1 min-w-0 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        <div className="mb-6">
          <div className="overline">Newsroom · Print</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight mt-1 flex items-center gap-2">
            <Printer className="w-7 h-7 text-[var(--accent)] shrink-0" />
            Convert to Print
          </h1>
          <p className="text-sm text-[var(--ink-muted)] mt-1 max-w-2xl">
            Pick articles from the Library, choose a target word count,
            and download a single .docx ready for your print designer.
            Selections stay put as you flip through pages and filter.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* ---------- Library list ---------- */}
          <div className="border border-[var(--border)] bg-white">
            <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
                <input
                  data-testid="print-search-input"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter by headline or author…"
                  className="w-full border border-[var(--border)] bg-[var(--bg)] pl-8 pr-8 py-1.5 text-sm focus:outline-none focus:border-[var(--slate-900)]"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                    aria-label="Clear filter"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <button
                type="button"
                data-testid="print-hide-processed-toggle"
                onClick={() => setHideProcessed((v) => !v)}
                disabled={!processedCount && !hideProcessed}
                className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] font-mono-display uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed ${
                  hideProcessed
                    ? "bg-[var(--accent)] border-[var(--accent)] text-white"
                    : "border-[var(--border)] hover:bg-[var(--secondary)]"
                }`}
                title={
                  processedCount
                    ? `${processedCount} article${processedCount === 1 ? " has" : "s have"} already been converted to print`
                    : "No article has been converted to print yet"
                }
              >
                <Printer className="w-3 h-3" />
                {hideProcessed
                  ? "Unprocessed only"
                  : `Hide processed${processedCount ? ` (${processedCount})` : ""}`}
              </button>
              <div
                data-testid="print-selection-count"
                className="text-xs font-mono-display text-[var(--ink-muted)]"
              >
                {selectedIds.size} selected · {filtered.length} article
                {filtered.length === 1 ? "" : "s"}
              </div>
              {selectedIds.size > 0 && (
                <button
                  data-testid="print-clear-selection"
                  onClick={clearAll}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                >
                  Clear selection
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="p-6 text-sm text-[var(--ink-muted)]">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                Loading articles…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-sm text-[var(--ink-muted)] text-center">
                No articles match this filter.
              </div>
            ) : (
              <ul className="divide-y divide-[var(--border)]">
                {pageSlice.map((a) => {
                  const checked = selectedIds.has(a.id);
                  return (
                    <li key={a.id}>
                      <label
                        data-testid={`print-row-${a.id}`}
                        className={
                          "flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-[var(--secondary)]/40 " +
                          (checked ? "bg-[var(--secondary)]/60" : "")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(a.id)}
                          data-testid={`print-check-${a.id}`}
                          className="mt-1 shrink-0 accent-[var(--accent)]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm text-[var(--ink)] leading-snug font-medium min-w-0">
                              {a.headline || "(untitled)"}
                            </div>
                            <PrintedBadges
                              usage={printUsage[a.id]}
                              clearedAt={printClearedAt[a.id]}
                              articleId={a.id}
                              busy={busyIds.has(a.id)}
                              onToggle={(cleared) =>
                                setCleared.mutate({ ids: [a.id], cleared })
                              }
                            />
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-[11px] text-[var(--ink-muted)] font-mono-display flex-wrap">
                            {a.writer_name && <span>BY {a.writer_name}</span>}
                            <span>· {relativeTime(a.created_at)}</span>
                            {a.wordpress_link && (
                              <span className="text-emerald-700">
                                · on WordPress
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Pagination footer */}
            {filtered.length > PAGE_SIZE && (
              <div className="px-4 py-3 border-t border-[var(--border)] flex items-center justify-between gap-3 text-xs">
                <div className="text-[var(--ink-muted)] font-mono-display">
                  Page {page} of {totalPages}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid="print-prev-page"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="p-1.5 border border-[var(--border)] disabled:opacity-30 hover:border-[var(--ink)]"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid="print-next-page"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="p-1.5 border border-[var(--border)] disabled:opacity-30 hover:border-[var(--ink)]"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ---------- Right sidebar: batch config + progress ---------- */}
          <div className="space-y-4">
            <div className="border border-[var(--border)] bg-white p-4">
              <div className="overline mb-2">Process selection</div>
              <label className="block text-xs text-[var(--ink-muted)] mb-1">
                Word count per article
              </label>
              <input
                data-testid="print-word-count"
                type="number"
                min={80}
                max={2500}
                step={25}
                value={wordCount}
                onChange={(e) => setWordCount(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
              />
              <p className="text-[10px] text-[var(--ink-muted)] mt-1">
                Default {defaultWords} words (change in Settings → Print
                edition).
              </p>

              <label
                htmlFor="print-paper-date"
                className="mt-3 flex items-center gap-1 text-xs text-[var(--ink-muted)] mb-1"
              >
                <CalendarDays className="w-3 h-3" />
                Paper date
              </label>
              <input
                id="print-paper-date"
                data-testid="print-paper-date"
                type="date"
                value={paperDate}
                onChange={(e) => setPaperDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
              />
              <p className="text-[10px] text-[var(--ink-muted)] mt-1">
                {paperDateOk
                  ? `Dates are rewritten for readers on ${formatPaperDate(paperDate)}: anything earlier becomes past tense, and "this Thursday"-style dates become real dates.`
                  : "The day this edition reaches readers. Required — events before it are rewritten as already past."}
              </p>

              {selectedProcessedCount > 0 && (
                <div
                  data-testid="print-selected-processed-warning"
                  className="mt-3 flex items-start gap-2 border px-2.5 py-2 text-xs bg-amber-50 text-amber-800 border-amber-300"
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <span>
                      {selectedProcessedCount} of the {selectedIds.size} selected{" "}
                      {selectedProcessedCount === 1 ? "article was" : "articles were"}{" "}
                      already converted to print. They're flagged under
                      "Show selected".
                    </span>
                    {selectedPrintedIds.length > 0 && (
                      <button
                        type="button"
                        data-testid="print-unmark-selected"
                        disabled={setCleared.isPending}
                        onClick={() =>
                          setCleared.mutate({
                            ids: selectedPrintedIds,
                            cleared: true,
                          })
                        }
                        title="For leftovers that didn't make the paper. The export history is not changed."
                        className="mt-1.5 flex items-center gap-1 underline hover:no-underline disabled:opacity-50"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Unmark {selectedPrintedIds.length === 1 ? "it" : `these ${selectedPrintedIds.length}`} as leftover
                        {selectedPrintedIds.length === 1 ? "" : "s"}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <button
                data-testid="print-process-btn"
                disabled={!canProcess}
                onClick={() => createJob.mutate()}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm bg-[var(--slate-900)] text-white hover:bg-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {createJob.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Printer className="w-4 h-4" />
                )}
                Process {selectedIds.size || ""} article
                {selectedIds.size === 1 ? "" : "s"}
              </button>

              {/* Photos for the same selection, as one .zip. Streams
                  straight to the browser's downloads — nothing to poll. */}
              <button
                type="button"
                data-testid="print-images-zip-btn"
                disabled={selectedIds.size === 0}
                onClick={() => {
                  window.location.assign(
                    api.printImagesZipUrl(orderedIds),
                  );
                  toast.success(
                    "Preparing images — the .zip will appear in your downloads.",
                  );
                }}
                title="Featured and body images of the selected articles, as JPEGs named after each headline and numbered in the same order as the .docx. Photo credits are listed in _image-credits.txt inside the zip."
                className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm border border-[var(--border)] text-[var(--ink)] hover:border-[var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ImageDown className="w-4 h-4" />
                Download images (.zip)
              </button>

              {selectedIds.size > 0 && (
                <details className="mt-3" data-testid="print-show-selected">
                  <summary className="text-xs cursor-pointer text-[var(--ink-muted)] hover:text-[var(--ink)]">
                    Show selected ({selectedIds.size}) — set the print order
                  </summary>
                  <p className="mt-1 text-[10px] text-[var(--ink-muted)]">
                    Drag the grip or use the arrows. The .docx and the
                    images .zip follow this order.
                  </p>
                  <PrintOrderList
                    articles={selectedArticles}
                    onMove={moveSelected}
                    badge={(a) =>
                      isProcessed(a.id) ? (
                        <span className="ml-1.5 text-[10px] font-mono-display uppercase tracking-wider text-violet-700">
                          · {printUsage[a.id].status === "printed" ? "printed" : "converting"}
                        </span>
                      ) : null
                    }
                  />
                </details>
              )}
            </div>

            {/* Active job progress */}
            {activeJob && (
              <div
                data-testid="print-active-job"
                className="border border-[var(--border)] bg-white p-4"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="overline">Current job</div>
                  <span
                    className={
                      "text-[10px] uppercase tracking-wider font-mono-display " +
                      (activeJob.status === "ready"
                        ? "text-emerald-700"
                        : activeJob.status === "error"
                        ? "text-red-700"
                        : "text-amber-700")
                    }
                  >
                    {activeJob.status}
                  </span>
                </div>
                <div className="text-xs text-[var(--ink-muted)] font-mono-display mb-2">
                  {activeJob.progress}/{activeJob.total} processed ·{" "}
                  {activeJob.word_count} words each
                  {activeJob.paper_date && (
                    <> · paper {formatPaperDate(activeJob.paper_date)}</>
                  )}
                </div>
                <div className="h-1.5 bg-[var(--secondary)] mb-3 overflow-hidden">
                  <div
                    className="h-full bg-[var(--accent)] transition-all"
                    style={{
                      width: `${
                        activeJob.total
                          ? (activeJob.progress / activeJob.total) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <ul className="text-[11px] space-y-1 max-h-64 overflow-y-auto">
                  {(activeJob.articles || []).map((a) => (
                    <li
                      key={a.article_id}
                      className="flex items-start gap-1.5 leading-snug"
                    >
                      <span
                        className={
                          "font-mono-display uppercase text-[9px] px-1 border shrink-0 mt-0.5 " +
                          (a.status === "ok"
                            ? "border-emerald-500 text-emerald-700"
                            : a.status === "error"
                            ? "border-red-500 text-red-700"
                            : "border-amber-500 text-amber-700")
                        }
                      >
                        {a.status}
                      </span>
                      <span className="min-w-0 flex-1">
                        {a.headline}
                        {a.error && (
                          <span className="block text-red-700 text-[10px]">
                            {a.error}
                          </span>
                        )}
                        {(a.date_changes || []).length > 0 && (
                          <details
                            className="mt-0.5"
                            data-testid={`print-date-changes-${a.article_id}`}
                          >
                            <summary className="cursor-pointer text-[10px] text-sky-800 hover:underline">
                              {a.date_changes.length} date change
                              {a.date_changes.length === 1 ? "" : "s"} — check before layout
                            </summary>
                            <ul className="mt-0.5 pl-3 list-disc text-[10px] text-[var(--ink-muted)] space-y-0.5">
                              {a.date_changes.map((note, k) => (
                                <li key={k}>{note}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {activeJob.status === "ready" && (
                  <a
                    href={api.printJobDownloadUrl(activeJob.id)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm bg-emerald-700 text-white hover:bg-emerald-800"
                  >
                    <Download className="w-4 h-4" />
                    Download .docx
                  </a>
                )}
              </div>
            )}

            {/* Prior jobs */}
            {priorJobs.length > 0 && (
              <div className="border border-[var(--border)] bg-white">
                <div className="px-4 py-3 border-b border-[var(--border)] overline">
                  Past exports
                </div>
                <ul className="divide-y divide-[var(--border)] max-h-72 overflow-y-auto">
                  {priorJobs.map((j) => (
                    <li
                      key={j.id}
                      data-testid={`print-past-job-${j.id}`}
                      className="px-4 py-2.5 flex items-center gap-2 hover:bg-[var(--secondary)]/40"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-[var(--ink)]">
                          {j.total} article{j.total === 1 ? "" : "s"} ·{" "}
                          {j.word_count} words · {j.status}
                          {j.paper_date && <> · paper {formatPaperDate(j.paper_date)}</>}
                        </div>
                        <div className="text-[10px] text-[var(--ink-muted)] font-mono-display">
                          {relativeTime(j.created_at)}
                        </div>
                      </div>
                      {j.status === "ready" && (
                        <a
                          href={api.printJobDownloadUrl(j.id)}
                          title="Download"
                          className="p-1.5 text-[var(--ink-muted)] hover:text-[var(--accent)]"
                        >
                          <FileDown className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <button
                        onClick={() => deleteJob.mutate(j.id)}
                        title="Delete this export (its articles will no longer be marked as printed)"
                        className="p-1.5 text-[var(--ink-muted)] hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
